import { Fragment, type ReactNode } from "react";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatDate, formatTime } from "../../lib/time";
import { bonPaymentLines, bonVatLines, dayName, type ReportPrint } from "./reports";

/** A line of the report: the name on the left, the amount on the right; `minor`: a detail of the line above. */
function Line({
  label,
  value,
  strong,
  minor,
}: {
  label: string;
  value: string | number;
  strong?: boolean;
  minor?: boolean;
}) {
  return (
    <div className={strong ? "kv zb-sub" : minor ? "kv zb-minor" : "kv"}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

/** The total of the report, white on a black card: what the report is about. */
function Total({ label, value }: { label: string; value: string }) {
  return (
    <div className="kv zb-total">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

/** A group of lines that belong together (the payment methods, the VAT, the checks), in a frame of its own. */
function Card({ children }: { children: ReactNode }) {
  return <div className="zb-card">{children}</div>;
}

/**
 * The X and Z receipt of the prototype: header, orders per channel, TOTAAL (only without platforms), the
 * online platforms with their day total only (Furkan, 12/10/2026: "enkel het totaal, minder detail") and
 * DAGTOTAAL, "Waarvan via platformen" for the linked platforms (already in the total: only how much of it came through
 * each), payment methods (with the platforms typed in: online under Bancontact, the rest under Contant), the
 * revenue incl. VAT per rate (the platforms in the 6%) and the checks. The X also has the drawer (change
 * float, cash of the till, CONTANT IN DE LADE (NU)) and the cancellations; the Z has none of those and no
 * name (Furkan, 11-12/10/2026: "geld in de lade moet er niet bij", "betaalwijze gewijzigd en annuleringen
 * hoeft niet op z-rapporten").
 *
 * The look (Furkan, 13/10/2026: "zo mooi perfect mogelijk, klas", the same information; of three drawings
 * he chose the modern one, "A zonder die nummer 1 van boven"): the letter of the till's screens, the day
 * large under a small title, the lines parted by dots, the total white on a black card, and the payment
 * methods, the VAT and the checks each in a frame. Only black and white: the printer knows no grey.
 */
export function ReportReceipt({ print }: { print: ReportPrint }) {
  const text = t.reports.bon;
  const { stats } = print;
  const x = print.kind === "x";
  const platformsTotal = print.platforms.reduce((total, platform) => total + platform.revenueCents, 0);
  const linkedTotal = print.linkedPlatforms.reduce((total, platform) => total + platform.revenueCents, 0);
  const heading = x ? text.xHeading : text.zHeading(print.number ?? 0);
  // "Dinsdag 13/10/2026"
  const day = print.businessDay ? dayName(print.businessDay) : "";

  return (
    <article className="bon zbon" aria-label={heading}>
      <div className="c zb-shop">{print.shop.name}</div>
      {/* the firm behind the shop right above its address, as large as the address and the VAT number (Furkan,
          13/10/2026: "Steakhouse And More bv moet er ook op, even groot als btw en adres, juist boven adres") */}
      <div className="c zb-addr">
        {[print.shop.company, print.shop.address, print.shop.vatNumber && text.vatNumber(print.shop.vatNumber)]
          .filter((line): line is string => Boolean(line))
          .map((line) => (
            <div key={line}>{line}</div>
          ))}
      </div>
      <div className="zb-head">
        {/* the Z says "Z Rapport" and its day, without its number (Furkan, 13/10/2026: "z rapport 1 moet er
            toch niet op de bon", then "Z Rapport Dinsdag 13/10/2026 moet daar opstaan"). The X keeps its small
            title: it is not the closing of the day. */}
        {x && <div className="zb-title">{heading}</div>}
        {/* the day it is about, big: a Z made the next afternoon still says which day (Furkan, 11/10/2026) */}
        {/* an X queued for printing by a server older than 11/10/2026 has no day */}
        {!x || print.businessDay ? (
          <div className="zb-day">
            {!x && <span>{text.zName}</span>}
            {!x && day !== "" && " "}
            {day !== "" && <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>}
          </div>
        ) : null}
        {/* from when to when there were orders, and when it was printed: on the X only (Furkan, 13/10/2026, of
            the Z: "deze info moet er niet op", "dit niet op de z rapport"); the archive says when a Z was closed */}
        {x && (
          <div className="zb-meta">
            {print.businessDay ? (
              <>
                {print.firstOrderAt !== null && print.lastOrderAt !== null
                  ? text.ordersFromTo(formatTime(print.firstOrderAt), formatTime(print.lastOrderAt))
                  : text.noOrders}
                <br />
              </>
            ) : null}
            {print.since && (
              <>
                {text.sinceZ(print.since.number, formatDate(print.since.closedAt), formatTime(print.since.closedAt))}
                <br />
              </>
            )}
            {text.printed(formatDate(print.at), formatTime(print.at))}
          </div>
        )}
        {!x && print.late && <div className="zb-late">{text.late}</div>}
      </div>
      {print.corrections.length > 0 && (
        <div className="zb-corr">
          <div className="zb-corr-title">{print.original ? text.original : text.corrected}</div>
          {print.original ? (
            <div>{text.originalNote(print.corrections.length)}</div>
          ) : (
            print.corrections.map((item) => (
              <div key={`${item.at}${item.orderCode}`}>
                {text.correction(
                  formatDate(item.at),
                  formatTime(item.at),
                  item.by,
                  item.orderCode,
                  formatEuro(item.totalCents),
                  item.reason,
                )}
              </div>
            ))
          )}
        </div>
      )}

      <div className="zb-list">
        <Line label={text.orders} value={stats.orders.count} />
        <Line label={t.reports.channels.takeaway} value={formatEuro(stats.byChannel.takeaway)} />
        <Line label={t.reports.channels.delivery} value={formatEuro(stats.byChannel.delivery)} />
        <Line label={t.reports.channels.table} value={formatEuro(stats.byChannel.table)} />
        {/* the platforms right under the channels: no line, no title and no total of the till on its own,
            DAGTOTAAL is the total (Furkan, 13/10/2026) */}
        {print.platforms.map((platform) => (
          <Line key={platform.name} label={platform.name} value={formatEuro(platform.revenueCents)} />
        ))}
        {print.platforms.length > 0 && <Line label={text.platformsTotal} value={formatEuro(platformsTotal)} strong />}
      </div>
      {print.platforms.length === 0 ? (
        <Total label={text.total} value={formatEuro(stats.orders.revenueCents)} />
      ) : (
        <Total label={text.dayTotal} value={formatEuro(stats.orders.revenueCents + platformsTotal)} />
      )}
      {/* the linked platforms (Antalya, 17/10/2026: "geen platformlijnen meer" since the koppeling): their orders are
          in the total above, so this only says how much of it came through each platform, and is never added */}
      {print.linkedPlatforms.length > 0 && (
        <Card>
          <div className="zb-open">{text.linkedTitle}</div>
          <div className="zb-note">{text.linkedNote}</div>
          {print.linkedPlatforms.map((platform) => (
            <Fragment key={platform.name}>
              <Line label={platform.name} value={formatEuro(platform.revenueCents)} />
              {platform.onlineCents !== undefined && (
                <Line label={text.linkedOnline} value={formatEuro(platform.onlineCents)} minor />
              )}
            </Fragment>
          ))}
          {print.linkedPlatforms.length > 1 && <Line label={text.linkedTotal} value={formatEuro(linkedTotal)} strong />}
        </Card>
      )}

      {/* the platforms too: paid online under Bancontact, the rest under Contant (Furkan, 13/10/2026) */}
      <Card>
        {bonPaymentLines(stats, print.platforms).map((line) => (
          <Line key={line.method} label={t.paymentMethods[line.method]} value={formatEuro(line.cents)} />
        ))}
      </Card>
      <Card>
        {bonVatLines(stats, print.platforms).map((vat) => (
          // the revenue incl. VAT per rate, as the shops write it in their kasboek (Furkan, 12/10/2026); 6, 12
          // and 21% each on their own line, the platforms in the 6% (13/10/2026)
          <Line key={vat.rate} label={text.vatLine(vat.rate)} value={formatEuro(vat.grossCents)} />
        ))}
      </Card>
      <Card>
        <Line label={text.discounts} value={formatEuro(stats.discountCents)} />
        <Line label={text.free} value={formatEuro(stats.free.totalCents)} />
        {x && (
          // no "Betaalwijze gewijzigd" on the X either (Furkan, 13/10/2026: "nooit meer weergeven")
          <Line
            label={text.cancelled}
            value={t.reports.countAmount(stats.cancelled.count, formatEuro(stats.cancelled.totalCents))}
          />
        )}
        {stats.tipsCents !== 0 && <Line label={text.tips} value={formatEuro(stats.tipsCents)} />}
        {(stats.thirdPartyCents ?? 0) !== 0 && (
          <Line label={text.thirdParty} value={formatEuro(stats.thirdPartyCents ?? 0)} />
        )}
      </Card>
      {x ? (
        <>
          <Card>
            <Line label={text.float} value={formatEuro(print.floatCents)} />
            <Line label={text.tillCash} value={formatEuro(stats.payments.cash)} />
            <Line label={text.drawerNow} value={formatEuro(print.expectedCashCents)} strong />
          </Card>
          {print.open.length > 0 && (
            <Card>
              <div className="zb-open">{text.stillOpen}</div>
              {print.open.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </Card>
          )}
          <div className="c zb-foot">
            {text.xFoot1}
            <br />
            {text.xFoot2}
          </div>
        </>
      ) : (
        <div className="c zb-foot">{text.kept}</div>
      )}
      <div className="bontafo">
        <img src="/tafo-logo.png" alt="Tafo" />
        <span>{t.receipt.tafo}</span>
      </div>
    </article>
  );
}
